import React, { useEffect, useState } from 'react'
import SEO from '../components/SEO'
import apiClient from '../services/api'
import defaultDoctors from '../data/defaultDoctors'
import useSiteSettings from '../hooks/useSiteSettings'

const values = [
  {
    title: 'Compassion',
    text: 'We listen carefully, respect patient concerns, and create an environment where people feel comfortable asking questions.'
  },
  {
    title: 'Clarity',
    text: 'We explain diagnoses, treatment plans, and next steps in language patients and families can actually use.'
  },
  {
    title: 'Continuity',
    text: 'We focus on follow-up and long-term wellness, not just one-time visits, so care feels connected and dependable.'
  }
]

const mapDoctors = (items = []) =>
  items.map((item) => ({
    name: item.name,
    specialty: item.specialty,
    experience: item.experience,
    achievements: item.achievements || [],
    summary: item.summary,
    imageUrl: item.imageUrl || item.image
  }))

const aboutHeroImage = 'https://res.cloudinary.com/dmiicgsbq/image/upload/v1776956288/uro3sffz0a9tv84tcugp.jpg'

const AboutPage = () => {
  const [cmsDoctors, setCmsDoctors] = useState([])
  const settings = useSiteSettings()

  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const res = await apiClient.get('/api/cms/doctors')
        setCmsDoctors(mapDoctors(res.data))
      } catch (error) {
        console.error('Error loading doctor highlights:', error)
      }
    }

    loadDoctors()
  }, [])

  const allDoctors = [...defaultDoctors, ...cmsDoctors]

  return (
    <div className="container py-5 page-grid-gap themed-page themed-about-page">
      <SEO
        title="About Us"
        description={`Learn about ${settings.siteName}'s mission, vision, expert doctors, and commitment to quality healthcare.`}
        keywords="about clinic, doctors, medical team, cardiology, orthopedics, pediatrics, experience"
        url="https://harmonymedical.com/about"
      />

      <section className="page-hero themed-page-hero about-hero">
        <div className="about-hero-layout">
          <div className="about-hero-copy">
            <span className="hero-kicker">About Our Clinic</span>
            <h1 className="mb-3">About {settings.siteName}</h1>
            <p className="lead">{settings.siteName} is a patient-focused healthcare provider offering a full range of clinical services in a caring, modern setting. Our multidisciplinary team is dedicated to delivering personalized treatment and preventive care to improve community wellness.</p>
            <p className="text-muted mb-0">
              We serve individuals and families who want more than a quick visit. Our approach centers on meaningful conversations, accurate assessment,
              coordinated care, and practical support that helps patients feel confident about their health decisions.
            </p>
          </div>

          <div className="about-hero-visual" aria-hidden="true">
            <div className="about-hero-image-shell">
              <img
                src={aboutHeroImage}
                alt="Doctors consulting in a modern hospital setting"
                className="about-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mb-5 editorial-section">
        <div className="editorial-heading-block">
          <span className="editorial-kicker">Direction</span>
          <h2 className="editorial-title">Mission & Vision</h2>
          <p className="editorial-lead">
            Our mission and vision reflect the standards of care, trust, and long-term community wellness we aim to deliver every day.
          </p>
        </div>
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card h-100 shadow-sm border-0 themed-feature-card">
              <div className="card-body">
                <h5 className="card-title">Mission</h5>
                <p className="card-text">To deliver accessible, high-quality medical care with compassion, innovation, and continuous improvement, ensuring every patient feels supported through every step of their health journey.</p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100 shadow-sm border-0 themed-feature-card">
              <div className="card-body">
                <h5 className="card-title">Vision</h5>
                <p className="card-text">To be the most trusted healthcare destination regionally, leading with excellence in clinical outcomes, exceptional patient experience, and community health empowerment.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-5 editorial-section editorial-section-soft">
        <div className="editorial-heading-block">
          <span className="editorial-kicker">Clinic Culture</span>
          <h2 className="editorial-title">Our Care Values</h2>
          <p className="editorial-lead">
            These values shape how our doctors, nurses, and support staff care for patients at every stage of the visit.
          </p>
        </div>
        <div className="row g-4">
          {values.map((value) => (
            <div key={value.title} className="col-md-4">
              <div className="card h-100 shadow-sm border-0 themed-feature-card">
                <div className="card-body">
                  <span className="themed-mini-badge" aria-hidden="true"></span>
                  <h5 className="card-title">{value.title}</h5>
                  <p className="card-text mb-0">{value.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-5 editorial-section">
        <div className="section-divider-wave"></div>
        <div className="editorial-heading-block">
          <span className="editorial-kicker">Specialists</span>
          <h2 className="editorial-title">Meet Our Doctors</h2>
          <p className="editorial-lead">
            Our medical team brings clinical experience, specialty knowledge, and a patient-first approach to every consultation.
          </p>
        </div>
        <div className="row g-4">
          {allDoctors.map((doc, idx) => (
            <div key={`${doc.name}-${idx}`} className="col-md-4">
              <div className="card h-100 shadow-sm overflow-hidden themed-doctor-card">
                <div className="themed-doctor-image-wrap">
                  <img src={doc.imageUrl} alt={doc.name} style={{ height: '240px', objectFit: 'cover' }} />
                </div>
                <div className="card-body">
                  <h5 className="card-title">{doc.name}</h5>
                  <h6 className="card-subtitle mb-2 text-muted">{doc.specialty}</h6>
                  <p className="mb-2">Experience: {doc.experience}</p>
                  <p>{doc.summary}</p>
                  <ul>
                    {(doc.achievements || []).map((ach, i) => (
                      <li key={`${doc.name}-achievement-${i}`}>{ach}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-5 editorial-section editorial-band">
        <div className="editorial-heading-block">
          <span className="editorial-kicker">Proof Points</span>
          <h2 className="editorial-title">Experience & Achievements</h2>
          <p className="editorial-lead">
            These milestones reflect the experience, quality standards, and patient confidence we continue to build as a clinic.
          </p>
        </div>
        <div className="row g-4">
          <div className="col-md-6">
            <div className="card h-100 border-0 shadow-sm p-4 themed-feature-card">
              <h5>Track Record</h5>
              <ul>
                <li>Over 15,000 patients served with over 95% satisfaction.</li>
                <li>Daily specialized clinics in cardiology, orthopedics, pediatrics, and more.</li>
                <li>100% compliance with safety and quality benchmarks.</li>
                <li>Dedicated patient guidance for preventive care, medication understanding, and long-term wellness goals.</li>
              </ul>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100 border-0 shadow-sm p-4 themed-feature-card">
              <h5>Accreditations</h5>
              <ul>
                <li>National Health Accreditation Board (NHAB) certified</li>
                <li>ISO 9001:2015 certified quality management</li>
                <li>Winner, Best Clinical Care Provider 2025</li>
                <li>Recognized for patient-centered service quality and clinic safety excellence.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage
